import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Image from "next/image";
import {
  IntegrationsService,
  DocInformation,
  SharepointSite,
  CredentialEntry,
} from "@/generated";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "../../SpotToast";
import { IOneDrivePickerItem } from "../OneDrivePickerField";
import { getFileImage } from "@/utils/generic";
import { AnimatePresence, motion } from "framer-motion";
import { SharepointSitePicker } from "./SharepointSitePicker";
import {
  GoogleDriveSourcePicker,
  MY_DRIVE_SOURCE,
  serializeGoogleDriveSource,
  type GoogleDriveSource,
} from "./GoogleDriveSourcePicker";
import { OneDrivePickerSelectedItem } from "./OneDrivePickerSelectedItem";
import { OneDriveFileExplorer } from "./OneDriveFileExplorer";
import { Button } from "@/components/shadcn/button";
import { useDisclosure } from "@/utils/useDisclosure";
import { NoxusFormLabel } from "../NoxusFormLabel";
import { Dialog, DialogContent, DialogTitle } from "@/components/shadcn/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/shadcn/select";
import { useQuery } from "@tanstack/react-query";
import { Plus, TriangleAlert } from "lucide-react";
import { Checkbox } from "@/components/shadcn/checkbox";

export type FilePickerServiceName = "OneDrive" | "Sharepoint" | "GoogleDrive";

/** Maps each service to its backend integration type key(s) to look up credentials.
 *  GoogleDrive accepts any Google integration whose declared OAuth scopes grant
 *  Drive read access (gspreadsheets / gdocs declare drive:browse → drive.readonly).
 *  The backend `gdrive_via_any` resolution picks the right credential at runtime. */
const SERVICE_INTEGRATION_TYPES: Record<FilePickerServiceName, string[]> = {
  OneDrive: ["onedrive", "azure_onedrive_sp"],
  Sharepoint: ["sharepoint", "azure_sharepoint_sp"],
  GoogleDrive: ["gdrive", "gspreadsheets", "gdocs"],
};

/** Human label shown next to a credential when the picker surfaces multiple
 *  Google integration types — disambiguates "Account jane@…" rows. */
const GOOGLE_INTEGRATION_LABELS: Record<string, string> = {
  gdrive: "Drive",
  gspreadsheets: "Sheets",
  gdocs: "Docs",
};

const SERVICE_META: Record<
  FilePickerServiceName,
  { label: string; image: string }
> = {
  OneDrive: {
    label: "OneDrive",
    image: "/images/ai-engine/external-integrations/onedrive.png",
  },
  Sharepoint: {
    label: "Sharepoint",
    image: "/images/ai-engine/external-integrations/sharepoint.png",
  },
  GoogleDrive: {
    label: "Google Drive",
    image: "/images/ai-engine/external-integrations/gdrive.png",
  },
};

interface CustomOneDrivePickerProps {
  onSelect: (items: IOneDrivePickerItem[]) => void;
  canMultiselect?: boolean;
  canSelectDirectories?: boolean;
  selectedItems?: IOneDrivePickerItem[];
  serviceName?: FilePickerServiceName;
  /** Google Drive only: restrict visible files by MIME type */
  mimeTypeFilter?: "spreadsheet" | "document" | "presentation";
  isDisabled?: boolean;
  /** When provided externally (from the credential picker field), the internal
   *  connection selector is hidden and this value is used instead. */
  credentialId?: string;
}

// Add this interface to extend DocInformation with an id field
export interface ExtendedDocInformation extends DocInformation {
  id?: string;
}

export interface ISharepointInfo {
  site: SharepointSite;
  subsite?: SharepointSite;
  drive: { id: string; name: string } | undefined;
}

// ---------------------------------------------------------------------------
// Hook: fetch credentials for the given service
// ---------------------------------------------------------------------------

/** Credential annotated with its parent integration type so the picker can
 *  badge "Sheets" / "Docs" alongside the user-visible display_name. */
export type ServiceCredential = CredentialEntry & {
  integration_type: string;
};

function useServiceCredentials(
  groupId: string,
  serviceName: FilePickerServiceName
) {
  const { data } = useQuery({
    queryKey: ["integration-config", groupId],
    queryFn: () =>
      IntegrationsService.getIntegrationConfig({
        path: { group_id: groupId },
      }),
    staleTime: 30_000,
  });

  return useMemo(() => {
    if (!data?.data) return [];
    const integrationTypes = SERVICE_INTEGRATION_TYPES[serviceName];
    const credentials: ServiceCredential[] = [];
    for (const integration of data.data.integrations) {
      if (!integrationTypes.includes(integration.type)) continue;
      for (const provider of integration.providers) {
        for (const cred of provider.credentials ?? []) {
          if (cred.status === "active") {
            credentials.push({ ...cred, integration_type: integration.type });
          }
        }
      }
    }
    return credentials;
  }, [data, serviceName]);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const CustomOneDrivePicker = ({
  onSelect,
  canMultiselect = false,
  canSelectDirectories = false,
  selectedItems = [],
  serviceName = "OneDrive",
  mimeTypeFilter,
  isDisabled = false,
  credentialId: externalCredentialId,
}: CustomOneDrivePickerProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { spotGroup } = useSpotUser();

  const meta = SERVICE_META[serviceName];
  const credentials = useServiceCredentials(spotGroup.id, serviceName);

  const [files, setFiles] = useState<ExtendedDocInformation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pathHistory, setPathHistory] = useState<
    { id: string; name: string }[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFolders, setShowFolders] = useState(true);
  const [fileTypeFilter, setFileTypeFilter] = useState<string>("all");
  const [tempSelectedItems, setTempSelectedItems] = useState<
    IOneDrivePickerItem[]
  >([]);
  const [internalCredentialId, setInternalCredentialId] = useState<
    string | undefined
  >(undefined);

  // GoogleDrive only — sidebar source selection (My Drive / Shared with me /
  // a specific Shared Drive). Defaults to My Drive so existing flows are
  // unchanged. Other services ignore this state. Declared early because
  // `fetchFiles` (defined below) reads it via closure.
  const [activeGoogleSource, setActiveGoogleSource] =
    useState<GoogleDriveSource>(MY_DRIVE_SOURCE);

  // Use externally provided credential when available, otherwise internal state.
  const hasExternalCredential =
    externalCredentialId != null && externalCredentialId !== "";
  const selectedCredentialId = hasExternalCredential
    ? externalCredentialId
    : internalCredentialId;

  const [cachedFiles, setCachedFiles] = useState<{
    [key: string]: { data: ExtendedDocInformation[]; fetchedAt: number };
  }>({});

  // Google Drive cursor-based pagination
  const [gdriveNextPageToken, setGdriveNextPageToken] = useState<string | null>(
    null
  );
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Monotonic request counter used to drop stale in-flight fetches (e.g. a
  // load-more that was triggered before the user changed folder / filter).
  const fetchRequestIdRef = useRef(0);

  // Auto-select first credential when credentials load (only when no external credential)
  useEffect(() => {
    if (
      !hasExternalCredential &&
      credentials.length > 0 &&
      !internalCredentialId
    ) {
      setInternalCredentialId(credentials[0].id);
    }
  }, [credentials, internalCredentialId, hasExternalCredential]);

  // Track the credential that was active when items were last synced from props,
  // so we can avoid copying stale selections after a credential switch.
  const lastSyncedCredRef = useRef(selectedCredentialId);

  // Reset cache and temporary selection when credential changes
  useEffect(() => {
    setCachedFiles({});
    setPathHistory([]);
    setFiles([]);
    setTempSelectedItems([]);
  }, [selectedCredentialId]);

  useEffect(() => {
    if (isOpen) {
      // Only copy parent's selectedItems if the credential hasn't changed since
      // they were set — otherwise we'd flash stale files from a different account.
      if (lastSyncedCredRef.current === selectedCredentialId) {
        setTempSelectedItems(selectedItems);
      } else {
        setTempSelectedItems([]);
      }
      setPathHistory([]);
      setSearchQuery("");
      setFileTypeFilter("all");
      setShowFolders(true);
    }
    lastSyncedCredRef.current = selectedCredentialId;
  }, [isOpen, selectedItems, selectedCredentialId]);

  useEffect(() => {
    if (
      isOpen &&
      (serviceName === "OneDrive" || serviceName === "GoogleDrive")
    ) {
      fetchFiles({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedCredentialId, activeGoogleSource]);

  // Reset navigation when the GoogleDrive source switches — paths from one
  // source (e.g. My Drive) don't make sense in another (e.g. a Shared Drive).
  useEffect(() => {
    if (serviceName !== "GoogleDrive") return;
    setPathHistory([]);
    setSearchQuery("");
    setGdriveNextPageToken(null);
  }, [activeGoogleSource, serviceName]);

  const [activeSharepoint, setActiveSharepoint] = useState<
    ISharepointInfo | undefined
  >(undefined);

  const effectiveSite = activeSharepoint?.subsite ?? activeSharepoint?.site;

  // The drive an item belongs to: the listing stamps it on every row, and the
  // active SharePoint drive is the fallback. All selection paths must agree —
  // filling this with the folder's own item id is what made SharePoint Read
  // Files call drives/<item-id>/items/<item-id> and 400 at run time.
  const resolveDriveId = useCallback(
    (item?: { drive_id?: string | null }) =>
      item?.drive_id || activeSharepoint?.drive?.id,
    [activeSharepoint?.drive?.id]
  );

  // Seed the breadcrumb with the Site Pages library so it is the selectable
  // folder (its page contents are leaves, not folder-selectable).
  useEffect(() => {
    const driveId = activeSharepoint?.drive?.id;
    if (!driveId || !canSelectDirectories) return;
    if (driveId.startsWith("sitepages:")) {
      setPathHistory([
        { id: driveId, name: activeSharepoint?.drive?.name || "Site Pages" },
      ]);
    }
  }, [
    activeSharepoint?.drive?.id,
    activeSharepoint?.drive?.name,
    canSelectDirectories,
  ]);

  const fetchFiles = useCallback(
    async ({
      folderId,
      driveId,
      siteId,
      searchQuery,
      pageToken,
    }: {
      folderId?: string;
      driveId?: string;
      siteId?: string;
      searchQuery?: string;
      pageToken?: string;
    }) => {
      const isAppending = !!pageToken;
      // Load-more stays on the current request version (it extends the same
      // context). A fresh fetch (folder/filter change) bumps the version so
      // any in-flight load-more from the previous context is dropped.
      const requestId = isAppending
        ? fetchRequestIdRef.current
        : ++fetchRequestIdRef.current;
      if (isAppending) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setGdriveNextPageToken(null);
      }
      const credKey = selectedCredentialId ?? "default";
      const sourceKey =
        serviceName === "GoogleDrive"
          ? serializeGoogleDriveSource(activeGoogleSource)
          : "_";
      const key = `${credKey}-${sourceKey}-${driveId || "root"}-${folderId || "root"}-${searchQuery || ""}`;
      try {
        if (!isAppending) {
          const cachedData = cachedFiles[key];
          const isDataFresh =
            cachedData && cachedData.fetchedAt > Date.now() - 1000 * 30 * 10;

          if (cachedData && isDataFresh) {
            if (requestId !== fetchRequestIdRef.current) return;
            setFiles(cachedData.data);
            setIsLoading(false);
            return;
          }
        }

        let data: DocInformation[] | undefined;

        if (serviceName === "Sharepoint") {
          if (!driveId || !siteId) {
            setIsLoading(false);
            SpotToast.error(
              "Please select a sharepoint site and source before proceeding."
            );
            return;
          }
          const response = await IntegrationsService.getSharepointDriveFiles({
            path: {
              group_id: spotGroup.id,
              drive_id: driveId,
              site_id: siteId,
            },
            query: {
              folder_id: folderId || undefined,
              query: searchQuery || undefined,
              credential_id: selectedCredentialId ?? undefined,
            },
          });
          data = response.data;
        } else if (serviceName === "GoogleDrive") {
          const response = await IntegrationsService.getGdriveFiles({
            path: { group_id: spotGroup.id },
            query: {
              folder_id: folderId || undefined,
              query: searchQuery || undefined,
              mime_type_filter: mimeTypeFilter ?? undefined,
              credential_id: selectedCredentialId ?? undefined,
              page_token: pageToken || undefined,
              source: serializeGoogleDriveSource(activeGoogleSource),
            },
          });
          const paginated = response.data;
          data = paginated.items;
          // Only publish the cursor if this response is still the active one —
          // prevents stale pagination tokens from leaking into a newer context.
          if (requestId === fetchRequestIdRef.current) {
            setGdriveNextPageToken(paginated.next_page_token ?? null);
          }
        } else {
          const response = await IntegrationsService.getOnedriveDriveFiles({
            path: {
              group_id: spotGroup.id,
              drive_id: driveId || "root",
            },
            query: {
              folder_id: folderId || undefined,
              query: searchQuery || undefined,
              credential_id: selectedCredentialId ?? undefined,
            },
          });
          data = response.data;
        }

        // Prefer the canonical provider ID (drive_metadata.id for GDrive /
        // OneDrive / SharePoint). The old `drive_id + title` fallback
        // collides distinct files with the same title in a folder.
        const filesWithIds = data.map((file) => ({
          ...file,
          id:
            file.drive_metadata?.id ||
            file.drive_id ||
            `file-${file.title}-${file.modified_at ?? ""}`,
        }));

        // Dedup within this response — some providers occasionally return
        // the same item twice (e.g. broken pagination, shortcut files).
        const dedupWithinResponse = (
          items: typeof filesWithIds
        ): typeof filesWithIds => {
          const seen = new Set<string>();
          const out: typeof filesWithIds = [];
          for (const item of items) {
            if (seen.has(item.id)) continue;
            seen.add(item.id);
            out.push(item);
          }
          return out;
        };
        const dedupedFiles = dedupWithinResponse(filesWithIds);

        // Drop results if the user moved on (e.g. changed folder/filter)
        // before this request returned. For non-append fetches this prevents
        // an old context's files from replacing the new context's list; for
        // load-more it prevents stale pagination from being appended.
        if (requestId !== fetchRequestIdRef.current) return;

        if (!isAppending) {
          const now = Date.now();
          setCachedFiles((prev) => ({
            ...prev,
            [key]: { data: dedupedFiles, fetchedAt: now },
          }));
          setFiles(dedupedFiles);
        } else {
          // Dedup against what's already shown, too: backend pagination can
          // re-return items from prior pages near filter/folder boundaries.
          setFiles((prev) => {
            const seen = new Set(prev.map((f) => f.id));
            const additions = dedupedFiles.filter((f) => !seen.has(f.id));
            return additions.length ? [...prev, ...additions] : prev;
          });
        }
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        if (status === 424) {
          SpotToast.error(
            `Your ${meta.label} connection needs to be reconnected. Go to Connections to fix it.`
          );
        } else {
          SpotToast.error(`Failed to load files from ${meta.label}`);
        }
      } finally {
        // Only clear the loading flags if we're still the latest request,
        // otherwise we'd flash the new fetch's spinner off prematurely.
        if (requestId === fetchRequestIdRef.current) {
          setIsLoading(false);
          setIsLoadingMore(false);
        }
      }
    },
    [
      cachedFiles,
      serviceName,
      spotGroup.id,
      meta.label,
      selectedCredentialId,
      mimeTypeFilter,
      activeGoogleSource,
    ]
  );

  const loadMoreFiles = useCallback(() => {
    if (!gdriveNextPageToken || isLoadingMore || serviceName !== "GoogleDrive")
      return;
    const currentFolder =
      pathHistory.length > 0
        ? pathHistory[pathHistory.length - 1].id
        : undefined;
    fetchFiles({ folderId: currentFolder, pageToken: gdriveNextPageToken });
  }, [
    gdriveNextPageToken,
    isLoadingMore,
    serviceName,
    pathHistory,
    fetchFiles,
  ]);

  const handleFileClick = useCallback(
    (file: ExtendedDocInformation) => {
      const fileId = file.drive_metadata?.id || file.drive_id;
      if (file.content_type === "folder") {
        setPathHistory([
          ...pathHistory,
          { id: fileId || "", name: file.title || "" },
        ]);

        if (fileId) {
          if (serviceName === "Sharepoint") {
            fetchFiles({
              folderId: fileId,
              siteId: effectiveSite?.id,
              driveId: activeSharepoint?.drive?.id,
            });
          } else {
            fetchFiles({ folderId: fileId });
          }
        }
      } else {
        const newItem: IOneDrivePickerItem = {
          id: fileId || `file-${file.title}`,
          name: file.title || "",
          content_type: file.content_type || "text/plain",
          url: file.url || "",
          image: getFileImage(file.content_type),
          drive_id: resolveDriveId(file),
          site_name: effectiveSite?.displayName,
        };

        if (canMultiselect) {
          const isAlreadySelected = tempSelectedItems.some(
            (item) => item.id === newItem.id
          );
          if (isAlreadySelected) {
            setTempSelectedItems(
              tempSelectedItems.filter((item) => item.id !== newItem.id)
            );
          } else {
            setTempSelectedItems([...tempSelectedItems, newItem]);
          }
        } else {
          setTempSelectedItems([newItem]);
        }
      }
    },
    [
      activeSharepoint?.drive?.id,
      resolveDriveId,
      effectiveSite?.id,
      effectiveSite?.displayName,
      canMultiselect,
      fetchFiles,
      pathHistory,
      serviceName,
      tempSelectedItems,
    ]
  );

  const handleFolderClick = useCallback(
    (file: ExtendedDocInformation) => {
      if (canSelectDirectories) {
        const newItem: IOneDrivePickerItem = {
          id:
            file.drive_metadata?.id || file.drive_id || `folder-${file.title}`,
          name: file.title || "",
          content_type: "folder",
          url: file.url || "",
          image: getFileImage(file.content_type),
          drive_id: resolveDriveId(file),
          site_name: effectiveSite?.displayName,
        };

        if (canMultiselect) {
          const isAlreadySelected = tempSelectedItems.some(
            (item) => item.id === newItem.id
          );
          if (isAlreadySelected) {
            setTempSelectedItems(
              tempSelectedItems.filter((item) => item.id !== newItem.id)
            );
          } else {
            setTempSelectedItems([...tempSelectedItems, newItem]);
          }
        } else {
          setTempSelectedItems([newItem]);
        }
      } else {
        handleFileClick(file);
      }
    },
    [
      resolveDriveId,
      effectiveSite?.displayName,
      canMultiselect,
      canSelectDirectories,
      handleFileClick,
      tempSelectedItems,
    ]
  );

  const navigateToBreadcrumb = useCallback(
    (index: number) => {
      const newHistory = pathHistory.slice(0, index + 1);
      setPathHistory(newHistory);
      const targetId = index >= 0 ? newHistory[index].id : "root";
      if (serviceName === "Sharepoint") {
        fetchFiles({
          folderId: targetId,
          siteId: effectiveSite?.id,
          driveId: activeSharepoint?.drive?.id,
        });
      } else {
        fetchFiles({ folderId: targetId });
      }
    },
    [
      activeSharepoint?.drive?.id,
      effectiveSite?.id,
      fetchFiles,
      pathHistory,
      serviceName,
    ]
  );

  useEffect(() => {
    if (!isOpen) return;

    const currentFolderId =
      pathHistory.length > 0
        ? pathHistory[pathHistory.length - 1].id
        : undefined;

    if (serviceName === "Sharepoint") {
      if (effectiveSite?.id && activeSharepoint?.drive?.id) {
        fetchFiles({
          siteId: effectiveSite?.id,
          driveId: activeSharepoint.drive.id,
          folderId: currentFolderId,
          searchQuery,
        });
      }
    } else {
      fetchFiles({ folderId: currentFolderId, searchQuery });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleConfirm = () => {
    if (
      canSelectDirectories &&
      tempSelectedItems.length === 0 &&
      pathHistory.length > 0
    ) {
      const current = pathHistory[pathHistory.length - 1];
      onSelect([
        {
          id: current.id,
          name: current.name,
          content_type: "folder",
          url: "",
          image: getFileImage("folder"),
          drive_id: resolveDriveId(files[0]),
          site_name: effectiveSite?.displayName,
        },
      ]);
    } else {
      onSelect(tempSelectedItems);
    }
    onClose();
  };

  const filteredFiles = useMemo(() => {
    // Dedup by id as a final safety net — regardless of where duplicates
    // sneak in (stale fetches, overlapping pagination, cache quirks), the
    // list the user sees must never contain the same item twice.
    const seenIds = new Set<string>();
    const dedupedFiles = files.filter((file, idx) => {
      const key = file.id ?? `__idx_${idx}`;
      if (seenIds.has(key)) return false;
      seenIds.add(key);
      return true;
    });
    return dedupedFiles
      .filter((file) => {
        if (!showFolders && file.content_type === "folder") return false;
        if (fileTypeFilter !== "all" && file.content_type !== "folder") {
          const type = file.content_type?.toLowerCase() || "";
          if (fileTypeFilter === "pdf" && !type.includes("pdf")) return false;
          if (
            fileTypeFilter === "word" &&
            !type.includes("wordprocessingml") &&
            !type.includes("msword") &&
            !type.includes("vnd.google-apps.document")
          )
            return false;
          if (
            fileTypeFilter === "excel" &&
            !type.includes("spreadsheetml") &&
            !type.includes("ms-excel") &&
            !type.includes("vnd.google-apps.spreadsheet")
          )
            return false;
          if (
            fileTypeFilter === "powerpoint" &&
            !type.includes("presentationml") &&
            !type.includes("ms-powerpoint") &&
            !type.includes("vnd.google-apps.presentation")
          )
            return false;
          if (fileTypeFilter === "image" && !type.includes("image/"))
            return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aIsFolder = a.content_type === "folder" ? 0 : 1;
        const bIsFolder = b.content_type === "folder" ? 0 : 1;
        if (aIsFolder !== bIsFolder) return aIsFolder - bIsFolder;
        return (a.title ?? "").localeCompare(b.title ?? "");
      });
  }, [files, showFolders, fileTypeFilter]);

  const handleChangeSharepointSite = (site: {
    site: SharepointSite;
    drive: { id: string; name: string } | undefined;
  }) => {
    setActiveSharepoint(site);
    setCachedFiles({});
    setPathHistory([]);
    if (site.site.id && site.drive?.id) {
      fetchFiles({
        siteId: site.site.id,
        driveId: site.drive?.id,
        searchQuery,
      });
    }
  };

  const removeFileFromSelection = (file: ExtendedDocInformation) => {
    setTempSelectedItems(
      tempSelectedItems.filter((item) => item.id !== file.drive_metadata?.id)
    );
  };

  const [tempSelectedItemsWithDelay, setTempSelectedItemsWithDelay] = useState<
    IOneDrivePickerItem[]
  >([]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setTempSelectedItemsWithDelay(tempSelectedItems);
    }, 200);
    return () => clearTimeout(timeout);
  }, [tempSelectedItems]);

  const showExplorer =
    serviceName !== "Sharepoint" || !!activeSharepoint?.drive?.id;

  return (
    <>
      <Button variant="outline" onClick={onOpen} isDisabled={isDisabled}>
        <Image
          alt={`${meta.label} logo`}
          src={meta.image}
          width={18}
          height={18}
        />
        {`Choose from ${meta.label}`}
      </Button>

      <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()} modal={false}>
        <DialogContent
          className="sm:max-w-5xl p-0 gap-0 overflow-hidden shadow-2xl flex flex-col"
          headerButtonsClassName="top-5 right-4"
          onInteractOutside={() => onClose()}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="flex h-[600px] min-h-0">
            {/* ── Sidebar ── */}
            <div className="w-60 border-r flex flex-col shrink-0 bg-muted/30">
              {/* Header */}
              <div className="p-4 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-background border border-border/60 flex items-center justify-center p-1.5 shadow-sm">
                    <Image
                      alt={`${meta.label} logo`}
                      src={meta.image}
                      width={22}
                      height={22}
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </div>
                  <DialogTitle className="text-base font-medium truncate">
                    {meta.label}
                  </DialogTitle>
                </div>
              </div>

              {/* Credential switcher — hidden when credential is managed externally */}
              {!hasExternalCredential && credentials.length > 0 && (
                <div className="px-4 pb-4 shrink-0">
                  <NoxusFormLabel label="Connection" />
                  <Select
                    value={selectedCredentialId ?? ""}
                    onValueChange={(v) => {
                      if (v === "__add_new__") {
                        window.open(
                          `/settings/administration/workspaces/${spotGroup.id}?tab=linked-accounts`,
                          "_blank"
                        );
                        return;
                      }
                      setInternalCredentialId(v);
                    }}
                  >
                    <SelectTrigger className="w-full text-sm h-9 bg-background">
                      <SelectValue placeholder="Select account…" />
                    </SelectTrigger>
                    <SelectContent>
                      {credentials.map((cred) => {
                        const baseLabel =
                          cred.display_name ?? `Account ${cred.id.slice(0, 6)}`;
                        // For Google Drive only: badge the integration type
                        // ("Drive" / "Sheets" / "Docs") so a user with
                        // multiple Google connections can tell them apart.
                        const typeBadge =
                          serviceName === "GoogleDrive"
                            ? GOOGLE_INTEGRATION_LABELS[cred.integration_type]
                            : undefined;
                        return (
                          <div key={cred.id} className="relative">
                            <SelectItem value={cred.id}>
                              {typeBadge
                                ? `${baseLabel} (${typeBadge})`
                                : baseLabel}
                            </SelectItem>
                            {cred.status !== "active" && (
                              <TriangleAlert
                                size={12}
                                className="text-destructive absolute right-7 top-1/2 -translate-y-1/2 pointer-events-none"
                              />
                            )}
                          </div>
                        );
                      })}
                      <SelectSeparator />
                      <SelectItem
                        value="__add_new__"
                        className="text-muted-foreground"
                      >
                        <span className="flex items-center gap-1.5">
                          <Plus size={12} className="shrink-0" />
                          Add new account
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Sharepoint site/drive pickers */}
              {serviceName === "Sharepoint" && (
                <div className="px-4 pb-4 shrink-0">
                  <SharepointSitePicker
                    activeSharepoint={activeSharepoint}
                    setActiveSharepoint={handleChangeSharepointSite}
                    credentialId={selectedCredentialId}
                  />
                </div>
              )}

              {/* Google Drive source list (My Drive / Shared with me / Shared drives) */}
              {serviceName === "GoogleDrive" && (
                <div className="px-4 pb-4 shrink-0">
                  <GoogleDriveSourcePicker
                    activeSource={activeGoogleSource}
                    setActiveSource={setActiveGoogleSource}
                    credentialId={selectedCredentialId}
                  />
                </div>
              )}

              {/* Filters */}
              <div className="px-4 pt-1 flex-1 overflow-y-auto">
                <p className="text-xs font-medium text-muted-foreground mb-3">
                  Filters
                </p>
                <div className="flex flex-col gap-4">
                  {/* File type filter — hidden for Google Drive when a mimeTypeFilter is set */}
                  {!(serviceName === "GoogleDrive" && mimeTypeFilter) && (
                    <div>
                      <NoxusFormLabel label="File type" />
                      <Select
                        value={fileTypeFilter}
                        onValueChange={setFileTypeFilter}
                      >
                        <SelectTrigger className="w-full text-sm h-9 bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All files</SelectItem>
                          <SelectItem value="pdf">PDF Documents</SelectItem>
                          <SelectItem value="word">Word Documents</SelectItem>
                          <SelectItem value="excel">
                            Excel Spreadsheets
                          </SelectItem>
                          <SelectItem value="powerpoint">PowerPoint</SelectItem>
                          <SelectItem value="image">Images</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <Checkbox
                      checked={showFolders}
                      onCheckedChange={(checked) =>
                        setShowFolders(checked === true)
                      }
                    />
                    <span className="text-sm font-medium leading-none">
                      Show folders
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* ── Main content ── */}
            <div className="flex-1 flex flex-col min-w-0 bg-background overflow-hidden">
              <AnimatePresence mode="wait">
                {showExplorer ? (
                  <motion.div
                    key="explorer"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-1 flex flex-col h-full overflow-hidden"
                  >
                    <OneDriveFileExplorer
                      pathHistory={pathHistory}
                      setPathHistory={setPathHistory}
                      activeSharepoint={activeSharepoint}
                      serviceName={serviceName}
                      fetchFiles={fetchFiles}
                      navigateToBreadcrumb={navigateToBreadcrumb}
                      handleFolderClick={handleFolderClick}
                      handleFileClick={handleFileClick}
                      removeFileFromSelection={removeFileFromSelection}
                      filteredFiles={filteredFiles}
                      isLoading={isLoading}
                      tempSelectedItems={tempSelectedItems}
                      canSelectDirectories={canSelectDirectories}
                      canMultiselect={canMultiselect}
                      searchQuery={searchQuery}
                      setSearchQuery={setSearchQuery}
                      onLoadMore={loadMoreFiles}
                      hasMore={!!gdriveNextPageToken}
                      isLoadingMore={isLoadingMore}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="empty"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex-1 flex items-center justify-center text-muted-foreground text-sm"
                  >
                    Please select a Sharepoint site and drive to view files.
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Selected Items Area */}
              {tempSelectedItemsWithDelay.length > 0 && (
                <div className="border-t px-4 py-3 shrink-0">
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    Selected items ({tempSelectedItemsWithDelay.length})
                  </p>
                  <div className="max-h-24 overflow-y-auto flex flex-wrap gap-1.5">
                    {tempSelectedItemsWithDelay.map((item) => (
                      <OneDrivePickerSelectedItem
                        key={item.id}
                        item={item}
                        tempSelectedItems={tempSelectedItems}
                        setTempSelectedItems={setTempSelectedItems}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t px-4 py-3 flex justify-end gap-2 shrink-0 bg-background">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>Select</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
